import {
    Item,
    itemSelectors,
    Movement,
    MovementDataStore,
    movementSelectors,
} from '@skinsoft/entity';
import { Location, LocationDataStore } from '@skinsoft/entity';
import { LinkableItemLifecycleStates } from '@skinsoft/entity-data-store';
import { LocationSelectorPopup } from '@skinsoft/entity-ui/src/location/location-selector-popup';
import { locationSelectors } from '@skinsoft/entity/src/location';
import { ReactiveVue } from '@skinsoft/reactive-vue';
import { getTranslatedString, StateInput, ValidationStates } from '@skinsoft/ui';
import { first } from 'rxjs/operators';
import { Component, Watch } from 'vue-property-decorator';

@Component({
    components: {
        sLocationSelectorPopup: LocationSelectorPopup,
        sStateInput: StateInput,
    },
    props: ['item', 'movement'],
    template: require('./location-input.html'),
})
export class LocationInput extends ReactiveVue {

    get realTimeLocation() {
        return itemSelectors.realTimeLocation(this.item);
    }

    get linkableItemState() {
        return movementSelectors.linkableItemState(this.movement, this.item.id);
    }

    public item: Item;
    public movement: Movement;

    public $locationDataStore: LocationDataStore;
    public $movementDataStore: MovementDataStore;
    public ValidationStates = ValidationStates;

    public readonly $refs: {
        stateInput: StateInput;
        locationPopup: LocationSelectorPopup;
    };

    public itemSelectors = itemSelectors;

    private selectedLocation: Location = null as any;
    private permanentLocationHint: string = "";

    private locations: Location[] | null = null;

    public mounted() {
        this.subscribeToSyncQueue(this.syncLinkableItemState.bind(this));
        this.triggerSync();
    }

    public async validator(value: string) {
        if (value) {
            try {
                this.selectedLocation = (await this.$locationDataStore.searchAll(value, null).pipe(first()).toPromise()).allData[0];
                if (!this.selectedLocation) {
                    this.selectedLocation = {id: value, translatedTitle: {translatedString: {fr: ""}}}
                }
                await this.$movementDataStore.transitionLinkableItem(
                    movementSelectors.id(this.movement),
                    itemSelectors.id(this.item),
                    LinkableItemLifecycleStates.FINALIZED,
                    locationSelectors.id(this.selectedLocation),
                );
                return true;
            } catch (error) {
                return false;
            }
        }
    }

    private syncLinkableItemState() {
        if (!this.$refs.stateInput) {
            // for some reason it is undefined for a while after mount
            return;
        }

        switch (this.linkableItemState) {
            case LinkableItemLifecycleStates.FINALIZED:
                this.$refs.stateInput.validate(this.realTimeLocation);
                break;
            case LinkableItemLifecycleStates.SELECTED:
            case LinkableItemLifecycleStates.IN_TRANSIT:
                this.$refs.stateInput.reset();
                break;
        }
    }

    private openPopup() {
        this.$refs.locationPopup.open()
    }

    private onLocationValidated(locationValidatedEvent) {
        const locationTitle = getTranslatedString(locationValidatedEvent.location.genericEntityTitle, this.$i18n.locale);
        this.$refs.stateInput.setValue(locationTitle)
        this.selectedLocation = locationValidatedEvent.location;
    }

    private subscribeToLocationHint() {
        // Retrieving permanent location as a hint to display
        const permanentLocationId = itemSelectors.permanentLocation(this.item);
        if (permanentLocationId) {
            this.subscribeTo(
                this.$locationDataStore.fetchOne(
                    permanentLocationId
                ).pipe(first()),
                async (location) => {
                    this.permanentLocationHint = await this.$locationDataStore.resolvePath(locationSelectors.pathIds(location), this.$i18n.locale)
                },
                'permanent-location-hint-subscription')
        }
    }

    private unsubscribeFromLocationHint() {
        this.unsubscribeFrom('permanent-location-hint-subscription')
    }

    @Watch('linkableItemState', { immediate: false, deep: true })
    private onLinkableItemStateChange() {
        this.triggerSync();
    }

    @Watch('item', { immediate: true })
    private onitemChange() {
        this.unsubscribeFromLocationHint();
        this.subscribeToLocationHint();
    }
}
